import axios from "axios"
import { AUTH_MODE, getAccessToken, triggerRelogin } from "@/auth/authStore"

/**
 * API-Client. Im Dev-Modus (VITE_AUTH_MODE=dev) wird der X-Dev-User-Header
 * gesetzt (Backend AUTH_MODE=dev); im Logto-Betrieb holt ein Request-
 * Interceptor das Access-Token (resource=VITE_LOGTO_API_RESOURCE) vom
 * AuthProvider und setzt es als Bearer-Header (Muster rmnc-personal).
 */
// BASE_URL endet mit "/" — Root: "/api/v1", Pfad-Betrieb: "/stammdaten/api/v1"
export const api = axios.create({ baseURL: `${import.meta.env.BASE_URL}api/v1` })

if (AUTH_MODE === "dev") {
  api.defaults.headers.common["X-Dev-User"] = JSON.stringify({
    sub: "dev-admin",
    name: "Dev Admin",
    roles: ["admin"],
  })
} else {
  api.interceptors.request.use(async (config) => {
    const token = await getAccessToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  })

  // 401 vom Backend (abgelaufene Session) -> einmaliger Re-Login-Redirect
  api.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error?.response?.status === 401) {
        triggerRelogin()
      }
      return Promise.reject(error)
    },
  )
}

export interface Organisation {
  id: string
  name: string
  name2: string | null
  kuerzel: string | null
  typen: string[]
  internet: string | null
  maildomain: string | null
  mailmuster: string | null
  zusatzinfo: string | null
  aktiv: boolean
  zentraleId: string | null
  // Nur in der Listen-Antwort befüllt (repräsentative Aggregation, Backend
  // `organisationen_service.kontaktspalten`) — im Detail/Liefer-API undefiniert.
  adresse?: string | null
  rufnummer?: string | null
  email?: string | null
}

export interface OrganisationInput {
  name: string
  name2?: string | null
  kuerzel?: string | null
  typen?: string[]
  internet?: string | null
  maildomain?: string | null
  mailmuster?: string | null
  zusatzinfo?: string | null
  zentraleId?: string | null
  aktiv?: boolean
}

export interface Page<T> {
  data: T[]
  total: number
  page: number
  size: number
}

export interface OrganisationenQuery {
  suche?: string
  typ?: string
  ort?: string
  nurAktive?: boolean
  page?: number
  size?: number
}

export async function fetchOrganisationen(
  query: OrganisationenQuery = {},
): Promise<Page<Organisation>> {
  const resp = await api.get<Page<Organisation>>("/organisationen", { params: query })
  return resp.data
}

export async function fetchOrganisation(id: string): Promise<Organisation> {
  const resp = await api.get<Organisation>(`/organisationen/${id}`)
  return resp.data
}

export async function createOrganisation(input: OrganisationInput): Promise<Organisation> {
  const resp = await api.post<Organisation>("/organisationen", input)
  return resp.data
}

export async function updateOrganisation(
  id: string,
  input: Partial<OrganisationInput>,
): Promise<Organisation> {
  const resp = await api.patch<Organisation>(`/organisationen/${id}`, input)
  return resp.data
}

export async function deactivateOrganisation(id: string): Promise<Organisation> {
  const resp = await api.delete<Organisation>(`/organisationen/${id}`)
  return resp.data
}

// --- Adressen (AP-2.3) ---------------------------------------------------

export interface Adresse {
  id: string
  organisationId: string | null
  strasse: string | null
  plz: string | null
  ort: string | null
  bundesland: string | null
  landkreis: string | null
  landId: string | null
  sprache: string | null
  anschrifttypen: string[]
  gueltig: boolean
  istStandort: boolean
  standortbezeichnung: string | null
  transportdauerTage: number | null
  bevorzugteAbholtage: string[]
  bevorzugteAnliefertage: string[]
  geoLat: number | null
  geoLon: number | null
  geocodedAt: string | null
}

export interface AdresseInput {
  organisationId?: string | null
  strasse?: string | null
  plz?: string | null
  ort?: string | null
  bundesland?: string | null
  landkreis?: string | null
  sprache?: string | null
  anschrifttypen?: string[]
  gueltig?: boolean
  istStandort?: boolean
  standortbezeichnung?: string | null
  transportdauerTage?: number | null
  bevorzugteAbholtage?: string[]
  bevorzugteAnliefertage?: string[]
}

export async function fetchAdressen(organisationId: string): Promise<Adresse[]> {
  const resp = await api.get<Adresse[]>("/adressen", { params: { organisationId } })
  return resp.data
}

export async function createAdresse(input: AdresseInput): Promise<Adresse> {
  const resp = await api.post<Adresse>("/adressen", input)
  return resp.data
}

export async function updateAdresse(
  id: string,
  input: Partial<AdresseInput>,
): Promise<Adresse> {
  const resp = await api.patch<Adresse>(`/adressen/${id}`, input)
  return resp.data
}

export async function deleteAdresse(id: string): Promise<void> {
  await api.delete(`/adressen/${id}`)
}

export async function geocodeAdresse(id: string, query: string): Promise<Adresse> {
  const resp = await api.post<Adresse>(`/adressen/${id}/geocode`, { query })
  return resp.data
}

// --- Kontakte & Kommunikation (AP-2.5) ------------------------------------

export interface Kontakt {
  id: string
  organisationId: string | null
  funktionId: string | null
  stammadresseId: string | null
  vorname: string | null
  nachname: string
  anrede: string | null
  geschlecht: string | null
  vorlagenanrede: string | null
  durchwahl: string | null
  employeeId: string | null
  employeeAnzeigename: string | null
  aktiv: boolean
}

export interface KontaktInput {
  organisationId?: string | null
  funktionId?: string | null
  stammadresseId?: string | null
  vorname?: string | null
  nachname: string
  anrede?: string | null
  geschlecht?: string | null
  vorlagenanrede?: string | null
  durchwahl?: string | null
  employeeId?: string | null
  aktiv?: boolean
}

export async function fetchKontakte(organisationId?: string): Promise<Kontakt[]> {
  const resp = await api.get<Kontakt[]>("/kontakte", { params: { organisationId } })
  return resp.data
}

export async function createKontakt(input: KontaktInput): Promise<Kontakt> {
  const resp = await api.post<Kontakt>("/kontakte", input)
  return resp.data
}

export async function updateKontakt(id: string, input: Partial<KontaktInput>): Promise<Kontakt> {
  const resp = await api.patch<Kontakt>(`/kontakte/${id}`, input)
  return resp.data
}

export async function deactivateKontakt(id: string): Promise<Kontakt> {
  const resp = await api.delete<Kontakt>(`/kontakte/${id}`)
  return resp.data
}

// --- Zertifikate (AP-2.6) --------------------------------------------------

export interface Zertifikat {
  id: string
  zertifikatsartId: string | null
  organisationId: string | null
  artikelId: string | null
  adresseId: string | null
  erstausstellung: string | null
  ampel: "gueltig" | "bald_ablaufend" | "abgelaufen" | null
  aktuellesAblaufdatum: string | null
}

export interface ZertifikatInput {
  zertifikatsartId?: string | null
  organisationId?: string | null
  artikelId?: string | null
  adresseId?: string | null
  erstausstellung?: string | null
}

export interface Zertifikatsart {
  id: string
  name: string
  gueltigkeitsdauerMonate: number | null
}

export async function fetchZertifikate(organisationId?: string): Promise<Zertifikat[]> {
  const resp = await api.get<Zertifikat[]>("/zertifikate", { params: { organisationId } })
  return resp.data
}

export async function createZertifikat(input: ZertifikatInput): Promise<Zertifikat> {
  const resp = await api.post<Zertifikat>("/zertifikate", input)
  return resp.data
}

export async function fetchZertifikatsarten(): Promise<Zertifikatsart[]> {
  const resp = await api.get<Zertifikatsart[]>("/zertifikatsarten")
  return resp.data
}

// --- Artikel (AP-2.7) -------------------------------------------------------

export interface Artikel {
  id: string
  artikelnummer: string | null
  kuerzel: string | null
  kurzbezeichnung: string | null
  beschreibung: string | null
  artikeltyp: string | null
  organisationId: string | null
  gueltig: boolean
}

export interface ArtikelInput {
  artikelnummer?: string | null
  kuerzel?: string | null
  kurzbezeichnung?: string | null
  beschreibung?: string | null
  artikeltyp?: string | null
  organisationId?: string | null
}

export async function fetchArtikel(suche?: string): Promise<Artikel[]> {
  const resp = await api.get<Artikel[]>("/artikel", { params: { suche } })
  return resp.data
}

export async function createArtikel(input: ArtikelInput): Promise<Artikel> {
  const resp = await api.post<Artikel>("/artikel", input)
  return resp.data
}

export async function updateArtikel(id: string, input: Partial<ArtikelInput>): Promise<Artikel> {
  const resp = await api.patch<Artikel>(`/artikel/${id}`, input)
  return resp.data
}

// --- Settings (Mandanten-Config, AP-2.2) ----------------------------------

export interface MandantConfig {
  organisationId: string | null
}

export async function fetchMandantConfig(): Promise<MandantConfig> {
  const resp = await api.get<MandantConfig>("/settings/mandant")
  return resp.data
}

export async function setMandantConfig(organisationId: string): Promise<MandantConfig> {
  const resp = await api.put<MandantConfig>("/settings/mandant", { organisationId })
  return resp.data
}

// --- Rufnummern (AP-2.5) ---------------------------------------------------

export interface Rufnummer {
  id: string
  organisationId: string | null
  kontaktId: string | null
  adresseId: string | null
  laendervorwahl: string | null
  vorwahl: string | null
  stammnummer: string | null
  durchwahl: string | null
  label: string | null
  typ: string | null
  anzeige: string | null
}

export interface RufnummerInput {
  organisationId?: string | null
  kontaktId?: string | null
  adresseId?: string | null
  laendervorwahl?: string | null
  vorwahl?: string | null
  stammnummer?: string | null
  durchwahl?: string | null
  label?: string | null
  typ?: string | null
}

export async function fetchRufnummern(params: {
  organisationId?: string
  kontaktId?: string
  adresseId?: string
}): Promise<Rufnummer[]> {
  const resp = await api.get<Rufnummer[]>("/rufnummern", { params })
  return resp.data
}

export async function createRufnummer(input: RufnummerInput): Promise<Rufnummer> {
  const resp = await api.post<Rufnummer>("/rufnummern", input)
  return resp.data
}

export async function updateRufnummer(
  id: string,
  input: Partial<RufnummerInput>,
): Promise<Rufnummer> {
  const resp = await api.patch<Rufnummer>(`/rufnummern/${id}`, input)
  return resp.data
}

export async function deleteRufnummer(id: string): Promise<void> {
  await api.delete(`/rufnummern/${id}`)
}

// --- E-Mails (AP-2.5) -------------------------------------------------------

export interface EMail {
  id: string
  organisationId: string | null
  kontaktId: string | null
  email: string
  labels: string[]
}

export interface EMailInput {
  organisationId?: string | null
  kontaktId?: string | null
  email: string
  labels?: string[]
}

export async function fetchEmails(params: {
  organisationId?: string
  kontaktId?: string
}): Promise<EMail[]> {
  const resp = await api.get<EMail[]>("/emails", { params })
  return resp.data
}

export async function createEmail(input: EMailInput): Promise<EMail> {
  const resp = await api.post<EMail>("/emails", input)
  return resp.data
}

export async function updateEmail(id: string, input: Partial<EMailInput>): Promise<EMail> {
  const resp = await api.patch<EMail>(`/emails/${id}`, input)
  return resp.data
}

export async function deleteEmail(id: string): Promise<void> {
  await api.delete(`/emails/${id}`)
}

// --- Kontaktfunktionen (AP-2.5, Lookup) -------------------------------------

export interface KontaktFunktion {
  id: string
  name: string
}

export async function fetchKontaktFunktionen(): Promise<KontaktFunktion[]> {
  const resp = await api.get<KontaktFunktion[]>("/kontakt-funktionen")
  return resp.data
}

export async function createKontaktFunktion(name: string): Promise<KontaktFunktion> {
  const resp = await api.post<KontaktFunktion>("/kontakt-funktionen", { name })
  return resp.data
}

// --- Zertifikate: Kontext-Liste (Organisation-Detail) -----------------------

export async function fetchZertifikatsartenMap(): Promise<Zertifikatsart[]> {
  return fetchZertifikatsarten()
}

export async function createZertifikatsart(
  name: string,
  gueltigkeitsdauerMonate?: number | null,
): Promise<Zertifikatsart> {
  const resp = await api.post<Zertifikatsart>("/zertifikatsarten", {
    name,
    gueltigkeitsdauerMonate: gueltigkeitsdauerMonate ?? null,
  })
  return resp.data
}

// --- Dokumente (AP-2.7) ------------------------------------------------------

export interface Dokument {
  id: string
  kategorie: string
  typ: string | null
  dokumentNr: string | null
  index: string | null
  bezeichnung: string | null
  beschreibung: string | null
  datum: string | null
  gueltig: boolean
  artikelId: string | null
  organisationId: string | null
  link: string | null
  paperlessId: string | null
}

export interface DokumentInput {
  kategorie: string
  typ?: string | null
  dokumentNr?: string | null
  index?: string | null
  bezeichnung?: string | null
  beschreibung?: string | null
  datum?: string | null
  gueltig?: boolean
  artikelId?: string | null
  organisationId?: string | null
}

export async function fetchDokumente(params: {
  artikelId?: string
  organisationId?: string
}): Promise<Dokument[]> {
  const resp = await api.get<Dokument[]>("/dokumente", { params })
  return resp.data
}

export async function createDokument(input: DokumentInput): Promise<Dokument> {
  const resp = await api.post<Dokument>("/dokumente", input)
  return resp.data
}

export async function deleteDokument(id: string): Promise<void> {
  await api.delete(`/dokumente/${id}`)
}

// --- Lieferbedingungen (AP-2.8) ----------------------------------------------

export interface Lieferbedingung {
  id: string
  incoterm: string
  name: string | null
  transportart: string | null
  risikouebergang: string | null
  verpflichtungenVerkaeufer: string | null
  verpflichtungenKaeufer: string | null
  kostenVerkaeufer: string | null
  kostenKaeufer: string | null
  versicherung: string | null
  norm: string | null
  aktiv: boolean
}

export interface LieferbedingungInput {
  incoterm: string
  name?: string | null
  transportart?: string | null
  risikouebergang?: string | null
  verpflichtungenVerkaeufer?: string | null
  verpflichtungenKaeufer?: string | null
  kostenVerkaeufer?: string | null
  kostenKaeufer?: string | null
  versicherung?: string | null
  norm?: string | null
  aktiv?: boolean
}

export async function fetchLieferbedingungen(
  nurAktive = true,
): Promise<Lieferbedingung[]> {
  const resp = await api.get<Lieferbedingung[]>("/lieferbedingungen", { params: { nurAktive } })
  return resp.data
}

export async function createLieferbedingung(
  input: LieferbedingungInput,
): Promise<Lieferbedingung> {
  const resp = await api.post<Lieferbedingung>("/lieferbedingungen", input)
  return resp.data
}

export async function updateLieferbedingung(
  id: string,
  input: Partial<LieferbedingungInput>,
): Promise<Lieferbedingung> {
  const resp = await api.patch<Lieferbedingung>(`/lieferbedingungen/${id}`, input)
  return resp.data
}

export async function deaktiviereLieferbedingung(id: string): Promise<Lieferbedingung> {
  const resp = await api.delete<Lieferbedingung>(`/lieferbedingungen/${id}`)
  return resp.data
}

export interface StandortLieferbedingung {
  id: string
  lieferbedingungId: string
  adresseId: string
  beschreibung: string | null
  standard: boolean
  transporttage: number | null
}

export interface StandortLieferbedingungInput {
  lieferbedingungId: string
  adresseId: string
  beschreibung?: string | null
  standard?: boolean
  transporttage?: number | null
}

export async function fetchStandortLieferbedingungen(
  adresseId?: string,
): Promise<StandortLieferbedingung[]> {
  const resp = await api.get<StandortLieferbedingung[]>("/standort-lieferbedingungen", {
    params: { adresseId },
  })
  return resp.data
}

export async function createStandortLieferbedingung(
  input: StandortLieferbedingungInput,
): Promise<StandortLieferbedingung> {
  const resp = await api.post<StandortLieferbedingung>("/standort-lieferbedingungen", input)
  return resp.data
}

export async function deleteStandortLieferbedingung(id: string): Promise<void> {
  await api.delete(`/standort-lieferbedingungen/${id}`)
}

// --- Zertifikatsarten-Pflege (AP-2.6) ---------------------------------------

export async function fetchZertifikatsartenListe(): Promise<Zertifikatsart[]> {
  return fetchZertifikatsarten()
}

// --- Textvorlagen (AP-2.8) ---------------------------------------------------

export interface Textvorlage {
  id: string
  kurztext: string | null
  vorlagetext: string
  typ: string | null
  position: string | null
  aktiv: boolean
  vorgabe: boolean
}

export interface TextvorlageInput {
  kurztext?: string | null
  vorlagetext: string
  typ?: string | null
  position?: string | null
  aktiv?: boolean
  vorgabe?: boolean
}

export async function fetchTextvorlagen(nurAktive = true): Promise<Textvorlage[]> {
  const resp = await api.get<Textvorlage[]>("/textvorlagen", { params: { nurAktive } })
  return resp.data
}

export async function createTextvorlage(input: TextvorlageInput): Promise<Textvorlage> {
  const resp = await api.post<Textvorlage>("/textvorlagen", input)
  return resp.data
}

export async function updateTextvorlage(
  id: string,
  input: Partial<TextvorlageInput>,
): Promise<Textvorlage> {
  const resp = await api.patch<Textvorlage>(`/textvorlagen/${id}`, input)
  return resp.data
}

export async function deleteTextvorlage(id: string): Promise<void> {
  await api.delete(`/textvorlagen/${id}`)
}

// --- Finanzen (AP-2.9, nur Rolle app:stammdaten:finanzen) --------------------

export interface Zahlungskondition {
  id: string
  organisationId: string
  bezeichnung: string | null
  zahlungszielTage: number | null
  skontoProzent: number | null
}

export interface ZahlungskonditionInput {
  organisationId: string
  bezeichnung?: string | null
  zahlungszielTage?: number | null
  skontoProzent?: number | null
}

export async function fetchZahlungskonditionen(
  organisationId?: string,
): Promise<Zahlungskondition[]> {
  const resp = await api.get<Zahlungskondition[]>("/finanzen/zahlungskonditionen", {
    params: { organisationId },
  })
  return resp.data
}

export async function createZahlungskondition(
  input: ZahlungskonditionInput,
): Promise<Zahlungskondition> {
  const resp = await api.post<Zahlungskondition>("/finanzen/zahlungskonditionen", input)
  return resp.data
}

export async function updateZahlungskondition(
  id: string,
  input: Partial<ZahlungskonditionInput>,
): Promise<Zahlungskondition> {
  const resp = await api.patch<Zahlungskondition>(`/finanzen/zahlungskonditionen/${id}`, input)
  return resp.data
}

export async function deleteZahlungskondition(id: string): Promise<void> {
  await api.delete(`/finanzen/zahlungskonditionen/${id}`)
}

export interface Bankkonto {
  id: string
  organisationId: string
  iban: string
  bic: string | null
  bank: string | null
  waehrung: string | null
  hauptbank: boolean
  ibanOk: boolean
}

export interface BankkontoInput {
  organisationId: string
  iban: string
  bic?: string | null
  bank?: string | null
  waehrung?: string | null
  hauptbank?: boolean
}

export async function fetchBankkonten(organisationId?: string): Promise<Bankkonto[]> {
  const resp = await api.get<Bankkonto[]>("/finanzen/bankkonten", { params: { organisationId } })
  return resp.data
}

export async function createBankkonto(input: BankkontoInput): Promise<Bankkonto> {
  const resp = await api.post<Bankkonto>("/finanzen/bankkonten", input)
  return resp.data
}

export async function updateBankkonto(
  id: string,
  input: Partial<BankkontoInput>,
): Promise<Bankkonto> {
  const resp = await api.patch<Bankkonto>(`/finanzen/bankkonten/${id}`, input)
  return resp.data
}

export async function deleteBankkonto(id: string): Promise<void> {
  await api.delete(`/finanzen/bankkonten/${id}`)
}
